import React from 'react';
import { Box, Card, CardContent, Typography, Grid } from '@mui/material';
import {
  DescriptionOutlined,
  CheckCircleOutlined,
  WarningAmberOutlined,
  ErrorOutlined
} from '@mui/icons-material';

/**
 * Summary Tile Component
 * Displays a single metric with icon - clickable for filtering
 * Horizontal layout matching app design pattern
 */
const SummaryTile = ({ icon, title, value, color, filterKey, isActive, onClick }) => (
  <Card
    onClick={() => onClick && onClick(filterKey)}
    sx={{
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s',
      border: isActive ? `2px solid` : '2px solid transparent',
      borderColor: isActive ? `${color}.main` : 'transparent',
      '&:hover': onClick ? {
        boxShadow: 3,
        transform: 'translateY(-2px)'
      } : {}
    }}
  >
    <CardContent sx={{ py: 2, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {/* Left: Label and Value */}
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ color: `${color}.main` }}>
            {value}
          </Typography>
        </Box>
        {/* Right: Icon */}
        <Box
          sx={{
            p: 1,
            borderRadius: 1,
            bgcolor: `${color}.50`,
            color: `${color}.main`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

/**
 * Document Summary Tiles Component
 * Displays summary metrics for organization documents
 * Clickable tiles for filtering
 *
 * Metrics:
 * - Total documents
 * - Active documents
 * - Expiring soon (within 30 days)
 * - Expired documents
 * - Pending review
 *
 * @param {Object} props
 * @param {Object} props.summary - Document summary data
 * @param {string} props.selectedFilter - Currently selected filter
 * @param {Function} props.onFilterChange - Handler for filter change
 */
const DocumentSummaryTiles = ({ summary, selectedFilter, onFilterChange }) => {
  if (!summary) {
    return null;
  }

  const tiles = [
    {
      icon: <DescriptionOutlined sx={{ fontSize: 24 }} />,
      title: 'Total Documents',
      value: summary.total || 0,
      color: 'primary',
      filterKey: null
    },
    {
      icon: <CheckCircleOutlined sx={{ fontSize: 24 }} />,
      title: 'Active',
      value: summary.active || 0,
      color: 'success',
      filterKey: 'active'
    },
    {
      icon: <WarningAmberOutlined sx={{ fontSize: 24 }} />,
      title: 'Expiring Soon',
      value: summary.expiringSoon || 0,
      color: 'warning',
      filterKey: 'expiringSoon'
    },
    {
      icon: <ErrorOutlined sx={{ fontSize: 24 }} />,
      title: 'Expired',
      value: summary.expired || 0,
      color: 'error',
      filterKey: 'expired'
    }
  ];

  return (
    <Grid container spacing={2}>
      {tiles.map((tile, index) => (
        <Grid item xs={6} sm={4} md={3} key={index}>
          <SummaryTile
            {...tile}
            isActive={selectedFilter === tile.filterKey}
            onClick={tile.filterKey ? onFilterChange : null}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default DocumentSummaryTiles;
